/**
 * ParticleRain — iter-99
 *
 * Standalone heart/flame rain. Extracted from `ReactionPanel.jsx` so the
 * sender's "memory replay" can fire the same animation when they open
 * their own card.
 *
 * Props:
 *   • trigger: number   — bump this to re-fire the animation
 *   • kind: "felt" | "candle"
 *   • count: number     — how many particles to render (caps internally at 20)
 *   • accent: string    — rgb color
 */
import React from "react";
import { motion } from "framer-motion";
import { Heart, Flame } from "lucide-react";

export default function ParticleRain({ trigger, kind, count, accent }) {
    if (!trigger || !count || count <= 0) return null;
    const cappedCount = Math.min(count, 20);
    const Glyph = kind === "felt" ? Heart : Flame;

    let viewportHeight = 800;
    try {
        viewportHeight =
            (typeof window !== "undefined" && (window.visualViewport?.height || window.innerHeight)) || 800;
    } catch { /* ignore */ }
    const maxLift = Math.max(360, viewportHeight * 0.72);

    const particles = Array.from({ length: cappedCount }).map((_, i) => {
        const offsetX = (i - (cappedCount - 1) / 2) * 14 + (Math.random() * 12 - 6);
        const liftJitter = 0.65 + Math.random() * 0.45;
        const lift = maxLift * liftJitter;
        const drift = (Math.random() * 48 - 24);
        const rot = (Math.random() * 90 - 45);
        const isBig = Math.random() < 0.32;
        const scale = isBig ? 1.4 + Math.random() * 0.4 : 0.5 + Math.random() * 0.5;
        const duration = 1.4 + Math.random() * 0.6;
        // Spread launches over a longer window for the memory replay
        // (count can be 20, would feel chaotic if all fired at once).
        const delay = i * 0.09 + Math.random() * 0.15;
        return { i, offsetX, lift, drift, rot, scale, delay, duration, isBig };
    });

    return (
        <div className="pointer-events-none fixed left-0 right-0 top-0 z-30" aria-hidden="true">
            {particles.map((p) => (
                <motion.span
                    key={`${trigger}-${p.i}`}
                    initial={{ opacity: 0, scale: 0.2, rotate: 0 }}
                    animate={{
                        opacity: [0, 1, 1, 0],
                        y: [0, -p.lift * 0.55, -p.lift],
                        x: [p.offsetX, p.offsetX + p.drift * 0.55, p.offsetX + p.drift],
                        scale: [0.2, p.scale * 1.05, p.scale * 0.85],
                        rotate: [0, p.rot * 0.6, p.rot],
                    }}
                    transition={{ duration: p.duration, delay: p.delay, ease: [0.32, 0.62, 0.36, 1] }}
                    className="absolute"
                    style={{
                        bottom: "92px",
                        left: "50%",
                        color: accent,
                        filter: p.isBig
                            ? `drop-shadow(0 6px 18px ${accent}99) drop-shadow(0 0 8px ${accent})`
                            : `drop-shadow(0 3px 9px ${accent}66)`,
                    }}
                >
                    <Glyph
                        className={p.isBig ? "w-6 h-6" : "w-3.5 h-3.5"}
                        strokeWidth={1.6}
                        fill={kind === "felt" ? accent : "none"}
                    />
                </motion.span>
            ))}
        </div>
    );
}
